import { Fragment, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "wouter";
import {
  ArrowLeft, Check, Copy, Crosshair, Download, ExternalLink, Info, Loader2, Plus, Presentation, RefreshCw, Replace, Share2, Sparkles, Trash2, Users, X,
} from "lucide-react";
import { aiBusy, api, ApiError, img, localTime, sourceLabel, STATUS_LABEL, type Detail } from "../lib/api";
import { useApp } from "../App";
import { ExportDialog } from "../components/ExportDialog";
import { AnalyzePanel } from "../components/AnalyzeMenu";
import { AddToCollection } from "../components/Collections";
import { Badge } from "./Library";

export function AssetView({ id }: { id: string }) {
  const [d, setD] = useState<Detail | null>(null);
  const [err, setErr] = useState("");
  const load = () => api<Detail>(`assets/${id}`).then(setD).catch((e) => setErr(e.message));
  useEffect(() => { setD(null); load(); }, [id]);

  const { isAdmin } = useApp();

  // Analysen körs i bakgrunden (servern direkt, Apple Vision via Mac-agenten) – hämta igen tills den är klar.
  useEffect(() => {
    if (!d) return;
    const waitingForAgent = aiBusy(d.asset);
    if (d.asset.analysis !== "pending" && !waitingForAgent) return;
    const t = setTimeout(load, d.asset.analysis === "pending" ? 1500 : 4000);
    return () => clearTimeout(t);
  }, [d]);

  if (err) return <p className="pt-8 text-bad">{err}</p>;
  if (!d) return <div className="pt-8 text-soft"><Loader2 className="animate-spin" /></div>;
  const a = d.asset;

  if (!isAdmin) return (
    <div className="pt-5">
      <Link href="/" className="mb-4 inline-flex items-center gap-1 text-soft hover:text-ink"><ArrowLeft size={15} /> Alla bilder</Link>
      <div className="grid gap-8 lg:grid-cols-[minmax(0,1.25fr)_minmax(380px,1fr)]">
        <div className="min-w-0 space-y-6">
          <div className="overflow-hidden rounded-xl border border-line" style={{ aspectRatio: `${a.width}/${a.height}`, background: `${a.color} url(${a.lqip}) center/cover` }}>
            <img src={img(a, 1600)} alt={a.alt} className="h-full w-full object-contain" />
          </div>
          <MoreLikeThis id={a.id} />
        </div>
        <div className="min-w-0 space-y-5">
          <ViewerInfo d={d} />
          <ViewerDownloads d={d} />
          <EmbedPanel d={d} />
        </div>
      </div>
    </div>
  );

  return (
    <div className="pt-5">
      <Link href="/" className="mb-4 inline-flex items-center gap-1 text-soft hover:text-ink"><ArrowLeft size={15} /> Alla bilder</Link>
      <div className="grid gap-8 lg:grid-cols-[minmax(0,1.25fr)_minmax(380px,1fr)]">
        <div className="min-w-0 space-y-6">
          <FocalPicker d={d} onChange={setD} />
          <CropPreviews d={d} />
          <MoreLikeThis id={a.id} admin hasPrint={!!d.asset.hasPrint} />
          <Facts d={d} />
        </div>
        <div className="min-w-0 space-y-5">
          <PublishBar d={d} onChange={setD} />
          <TagPanel d={d} onChange={setD} />
          <CollectionsCard d={d} onChange={load} />
          <MetaForm d={d} onChange={setD} />
          <EmbedPanel d={d} />
          <Usage d={d} />
          <Similar d={d} />
          <Actions d={d} onChange={setD} />
          <History d={d} />
        </div>
      </div>
    </div>
  );
}

// ---------- Fler som denna ----------
type Similar = { method: "vision" | "tags" | "none"; hasPrint: boolean;
  items: { id: string; title: string; color: string; lqip: string; width: number; height: number; version: number; score: number }[] };

function MoreLikeThis({ id, admin, hasPrint }: { id: string; admin?: boolean; hasPrint?: boolean }) {
  const [s, setS] = useState<Similar | null>(null);
  useEffect(() => { setS(null); api<Similar>(`assets/${id}/similar`).then(setS).catch(() => {}); }, [id, hasPrint]);
  if (!s || !s.items.length) return null;
  return (
    <div>
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <div className="h-section">Fler som denna</div>
        <span className="text-[12px] text-muted">
          {s.method === "vision" ? "Liknande motiv enligt Apple Vision" : "Utifrån taggar och färger"}
          {admin && s.method !== "vision" && !hasPrint && " – analysera med Apple Vision för bättre träffar"}
        </span>
      </div>
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 xl:grid-cols-6">
        {s.items.map((x) => (
          <Link key={x.id} href={`/bild/${x.id}`} title={x.title} className="group block overflow-hidden rounded-lg border border-line">
            <img src={img(x, 320, "1:1")} alt={x.title} loading="lazy" className="aspect-square w-full object-cover transition group-hover:scale-105"
              style={{ background: `${x.color} url(${x.lqip}) center/cover` }} />
          </Link>
        ))}
      </div>
    </div>
  );
}

function CollectionsCard({ d, onChange }: { d: Detail; onChange: () => void }) {
  const [msg, setMsg] = useState("");
  return (
    <div className="card p-4">
      <div className="mb-2 flex items-center justify-between">
        <div className="h-section">Samlingar</div>
        <AddToCollection ids={[d.asset.id]} label="Lägg till" onDone={(m) => { setMsg(m); onChange(); }} />
      </div>
      {d.collections.length === 0 ? <p className="text-[13px] text-muted">Inte med i någon samling.</p> : (
        <div className="flex flex-wrap gap-1.5">
          {d.collections.map((c) => (
            <span key={c.id} className="chip">
              <Link href={`/?collection=${c.id}`} className="hover:text-ember">{c.name}</Link>
              <button aria-label={`Ta bort ur ${c.name}`} className="text-muted hover:text-bad" onClick={async () => {
                await api(`collections/${c.id}/assets`, { method: "DELETE", json: { ids: [d.asset.id] } }); onChange();
              }}><X size={12} /></button>
            </span>
          ))}
        </div>
      )}
      {msg && <p className="mt-2 text-[12px] text-soft">{msg}</p>}
    </div>
  );
}

// ---------- Läsvy för vanliga användare ----------
function ViewerInfo({ d }: { d: Detail }) {
  const { status } = useApp();
  const a = d.asset;
  const rows: [string, React.ReactNode][] = [
    ["Alt-text", a.alt],
    ["Beskrivning", a.description],
    ["Fotograf", a.photographer],
    ["Plats", a.place],
    ["Licens", [status?.licenses[a.license], a.license_expires && `t.o.m. ${a.license_expires}`].filter(Boolean).join(" ")],
    ["Villkor", a.license_note],
    ["Storlek", `${a.width} × ${a.height} px`],
  ];
  return (
    <div className="card p-4">
      <h1 className="text-xl font-semibold tracking-tight">{a.title || "Namnlös"}</h1>
      <dl className="mt-3 grid grid-cols-[100px_1fr] gap-x-4 gap-y-1.5 text-[13px]">
        {rows.filter(([, v]) => v).map(([k, v]) => (<Fragment key={k}><dt className="text-muted">{k}</dt><dd>{v}</dd></Fragment>))}
      </dl>
      {d.tags.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {d.tags.map((t) => <Link key={t.name} href={`/?tags=${encodeURIComponent(t.name)}`} className="chip text-soft hover:text-ink">{t.name}</Link>)}
        </div>
      )}
      {d.collections.length > 0 && (
        <p className="mt-3 text-[13px] text-soft">Finns i: {d.collections.map((c, i) => (
          <span key={c.id}>{i > 0 && ", "}<Link href={`/?collection=${c.id}`} className="text-ember hover:underline">{c.name}</Link></span>
        ))}</p>
      )}
    </div>
  );
}

function ViewerDownloads({ d }: { d: Detail }) {
  const [exporting, setExporting] = useState<"social" | "pptx" | null>(null);
  return (
    <div className="card p-4">
      <div className="h-section mb-2">Ladda ner</div>
      <div className="flex flex-wrap gap-2">
        <a className="btn" href={d.links.original}><Download size={14} /> Original</a>
        <button className="btn" onClick={() => setExporting("social")}><Share2 size={14} /> Sociala medier</button>
        <button className="btn" onClick={() => setExporting("pptx")}><Presentation size={14} /> PowerPoint</button>
      </div>
      {d.asset.photographer && <p className="mt-2 text-[12px] text-soft">Ange alltid ”Foto: {d.asset.photographer}” när bilden används.</p>}
      {exporting && <ExportDialog items={[d.asset]} initial={exporting} onClose={() => setExporting(null)} />}
    </div>
  );
}

// ---------- Bild + fokuspunkt ----------
function FocalPicker({ d, onChange }: { d: Detail; onChange: (d: Detail) => void }) {
  const a = d.asset;
  const [pt, setPt] = useState({ x: a.focal_x, y: a.focal_y });
  useEffect(() => setPt({ x: a.focal_x, y: a.focal_y }), [a.focal_x, a.focal_y]);
  const save = async (e: React.MouseEvent<HTMLDivElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    const p = { x: +((e.clientX - r.left) / r.width).toFixed(3), y: +((e.clientY - r.top) / r.height).toFixed(3) };
    setPt(p);
    onChange(await api<Detail>(`assets/${a.id}`, { method: "PATCH", json: { focal_x: p.x, focal_y: p.y } }));
  };
  return (
    <div>
      <div className="relative cursor-crosshair overflow-hidden rounded-xl border border-line" onClick={save}
        style={{ aspectRatio: `${a.width}/${a.height}`, background: `${a.color} url(${a.lqip}) center/cover` }}>
        <img src={img(a, 1600)} alt={a.alt} className="h-full w-full object-contain" draggable={false} />
        <div className="pointer-events-none absolute -translate-x-1/2 -translate-y-1/2" style={{ left: `${pt.x * 100}%`, top: `${pt.y * 100}%` }}>
          <div className="h-9 w-9 rounded-full border-2 border-white shadow-[0_0_0_1px_rgba(0,0,0,.4),0_2px_8px_rgba(0,0,0,.4)]" />
          <div className="absolute left-1/2 top-1/2 h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white" />
        </div>
      </div>
      <p className="mt-2 flex items-center gap-1.5 text-[13px] text-soft">
        <Crosshair size={14} /> Klicka i bilden för att sätta fokuspunkt – alla beskärningar centreras kring den.
      </p>
    </div>
  );
}

const RATIOS = ["16:9", "3:2", "4:5", "1:1"];
function CropPreviews({ d }: { d: Detail }) {
  const a = d.asset;
  const k = `${a.focal_x}-${a.focal_y}`;
  return (
    <div>
      <div className="h-section mb-2">Så här beskärs bilden</div>
      <div className="grid grid-cols-4 items-end gap-3">
        {RATIOS.map((r) => {
          const [w, h] = r.split(":").map(Number);
          return (
            <figure key={r}>
              <img key={k} src={`${img(a, 480, r)}&f=${k}`} alt="" className="w-full rounded-lg border border-line" style={{ aspectRatio: `${w}/${h}`, background: a.color }} />
              <figcaption className="mt-1 text-center text-[12px] text-muted">{r}</figcaption>
            </figure>
          );
        })}
      </div>
    </div>
  );
}

function Facts({ d }: { d: Detail }) {
  const a = d.asset;
  const rows: [string, string | null][] = [
    ["Fil", `${a.filename} · ${(a.bytes / 1e6).toFixed(1)} MB`],
    ["Storlek", `${a.width} × ${a.height} px`],
    ["Fotograferad", a.taken_at ? new Date(a.taken_at).toLocaleString("sv-SE", { dateStyle: "long", timeStyle: "short" }) : null],
    ["Kamera", a.camera],
    ["GPS", a.lat != null ? `${a.lat.toFixed(4)}, ${a.lng!.toFixed(4)}` : null],
    ["Personer (AI)", a.people_count != null ? String(a.people_count) : null],
    ["Text i bilden", a.ocr_text || null],
    ["Version", `${a.version}`],
    ["Id", a.id],
  ];
  return (
    <dl className="card grid grid-cols-[120px_1fr] gap-x-4 gap-y-1.5 p-4 text-[13px]">
      {rows.filter(([, v]) => v).map(([k, v]) => (<Fragment key={k}><dt className="text-muted">{k}</dt><dd className="break-words">{v}</dd></Fragment>))}
    </dl>
  );
}

// ---------- Publicering ----------
function PublishBar({ d, onChange }: { d: Detail; onChange: (d: Detail) => void }) {
  const a = d.asset;
  const [problems, setProblems] = useState<string[]>([]);
  const setStatus = async (status: string) => {
    try { onChange(await api<Detail>(`assets/${a.id}`, { method: "PATCH", json: { status } })); setProblems([]); }
    catch (e) { if (e instanceof ApiError) setProblems(e.body.problems ?? [e.message]); }
  };
  return (
    <div className="card p-4">
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <div className="text-[12px] text-muted">Status</div>
          <div className="flex items-center gap-2 font-semibold">
            <span className={`h-2 w-2 rounded-full ${a.status === "publicerad" ? (d.blocked ? "bg-bad" : "bg-ok") : a.status === "utkast" ? "bg-warn" : "bg-muted"}`} />
            {STATUS_LABEL[a.status]}{a.status === "publicerad" && d.blocked && <span className="font-normal text-bad">– visas inte: {d.blocked}</span>}
          </div>
        </div>
        {a.status !== "publicerad" && <button className="btn-primary" onClick={() => setStatus("publicerad")}><Check size={15} /> Publicera</button>}
        {a.status === "publicerad" && <button className="btn" onClick={() => setStatus("utkast")}>Avpublicera</button>}
      </div>
      {(problems.length > 0 || (a.status !== "publicerad" && d.publishProblems.length > 0)) && (
        <ul className="mt-3 space-y-1 rounded-lg bg-warn-soft p-3 text-[13px] text-warn">
          {(problems.length ? problems : d.publishProblems).map((p) => <li key={p}>• {p}</li>)}
        </ul>
      )}
    </div>
  );
}

// ---------- Taggar ----------
function TagPanel({ d, onChange }: { d: Detail; onChange: (d: Detail) => void }) {
  const { status } = useApp();
  const a = d.asset;
  const [input, setInput] = useState("");
  const [all, setAll] = useState<{ name: string; n: number }[]>([]);
  useEffect(() => { api<{ name: string; n: number }[]>("tags").then(setAll); }, []);
  const has = new Set(d.tags.map((t) => t.name));
  const add = async (name: string) => {
    if (!name.trim()) return;
    onChange(await api<Detail>(`assets/${a.id}/tags`, { method: "POST", json: { name } }));
    setInput("");
  };
  const remove = async (name: string) => onChange(await api<Detail>(`assets/${a.id}/tags/${encodeURIComponent(name)}`, { method: "DELETE" }));
  const decide = async (name: string, action: "accept" | "reject") =>
    onChange(await api<Detail>(`assets/${a.id}/suggestions`, { method: "POST", json: { action, name } }));
  const acceptAll = async () => onChange(await api<Detail>(`assets/${a.id}/suggestions`, { method: "POST", json: { action: "accept-all", min: 0.6 } }));

  const matches = input.trim()
    ? all.filter((t) => t.name.includes(input.trim().toLowerCase()) && !has.has(t.name)).slice(0, 6) : [];
  const quick = (status?.vocabulary ?? []).filter((v) => !has.has(v)).slice(0, 12);
  const sure = d.suggestions.filter((s) => s.confidence >= 0.6).length;

  return (
    <div className="card p-4">
      <div className="h-section mb-2">Taggar</div>
      <div className="flex flex-wrap gap-1.5">
        {d.tags.length === 0 && <span className="text-muted">Inga taggar ännu.</span>}
        {d.tags.map((t) => (
          <span key={t.name} className="chip" title={`${t.category} · ${t.source}`}>
            {t.name}
            <button onClick={() => remove(t.name)} className="text-muted hover:text-bad" aria-label={`Ta bort ${t.name}`}><X size={12} /></button>
          </span>
        ))}
      </div>
      <form className="relative mt-3" onSubmit={(e) => { e.preventDefault(); add(input); }}>
        <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Skriv en tagg och tryck Enter" className="field" />
        {matches.length > 0 && (
          <div className="absolute inset-x-0 top-full z-10 mt-1 overflow-hidden rounded-lg border border-line bg-card shadow-lg">
            {matches.map((m) => (
              <button type="button" key={m.name} onClick={() => add(m.name)} className="flex w-full justify-between px-3 py-1.5 text-left hover:bg-mist">
                {m.name} <span className="text-muted">{m.n}</span>
              </button>
            ))}
          </div>
        )}
      </form>
      {quick.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {quick.map((v) => (
            <button key={v} onClick={() => add(v)} className="rounded-full px-2 py-0.5 text-[12px] text-soft hover:bg-mist">+ {v}</button>
          ))}
        </div>
      )}

      <div className="mt-4 border-t border-line pt-3">
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center gap-1.5 font-medium"><Sparkles size={15} className="text-ember" /> Förslag</div>
          {sure > 1 && <button className="text-[13px] font-medium text-ember hover:underline" onClick={acceptAll}>Godkänn {sure} säkra</button>}
        </div>
        {a.analysis === "pending" && <p className="flex items-center gap-1.5 text-soft"><Loader2 size={14} className="animate-spin" /> Analyserar bilden…</p>}
        {a.analysis !== "pending" && d.suggestions.length === 0 && <p className="text-muted">Inga fler förslag.</p>}
        <div className="flex flex-wrap gap-1.5">
          {d.suggestions.map((s) => (
            <span key={s.name} className="group inline-flex items-center overflow-hidden rounded-full border border-dashed border-ember/50 bg-ember-soft/50 text-[13px]"
              title={`${Math.round(s.confidence * 100)} % · ${sourceLabel(s.source)}\n${s.reason}`}>
              <button onClick={() => decide(s.name, "accept")} className="flex items-center gap-1.5 py-0.5 pl-2.5 pr-1.5 hover:bg-ember-soft">
                <Plus size={12} className="text-ember" /> {s.name}
                <span className="h-1 w-6 overflow-hidden rounded-full bg-ember/15"><span className="block h-full bg-ember" style={{ width: `${s.confidence * 100}%` }} /></span>
              </button>
              <button onClick={() => decide(s.name, "reject")} className="py-0.5 pl-1 pr-2 text-muted hover:text-bad" aria-label={`Avvisa ${s.name}`}><X size={12} /></button>
            </span>
          ))}
        </div>
        {d.suggestions.length > 0 && <p className="mt-2 text-[12px] text-muted">Håll muspekaren över ett förslag för att se varför det föreslås.</p>}
      </div>
      <AiStatus d={d} onChange={onChange} />
    </div>
  );
}

// ---------- AI-analys ----------
const AI_LABEL: Record<string, string> = { none: "Inte analyserad", pending: "I kö", claimed: "Analyseras…", done: "Klar", failed: "Misslyckades" };

function AiStatus({ d, onChange }: { d: Detail; onChange: (d: Detail) => void }) {
  const { status } = useApp();
  const a = d.asset;
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const agentMode = status?.ai.visionMode === "agent";
  const visionText = a.vision === "pending" && agentMode && !status?.agent?.online ? "Väntar på Mac-agenten (offline)"
    : a.vision === "claimed" && agentMode ? "Mac-agenten analyserar…" : AI_LABEL[a.vision] ?? a.vision;
  const row = (label: string, state: string, text: string, enabled: boolean) => (
    <div className="flex items-center justify-between text-[13px]">
      <span className="text-soft">{label}</span>
      <span className={`flex items-center gap-1.5 ${state === "done" ? "text-ok" : state === "failed" ? "text-bad" : enabled ? "text-soft" : "text-muted"}`}>
        {(state === "pending" || state === "claimed") && <Loader2 size={12} className="animate-spin" />}
        {state === "done" && <Check size={13} />}
        {enabled ? text : "Inte tillgänglig"}
      </span>
    </div>
  );
  return (
    <div className="mt-4 border-t border-line pt-3">
      <div className="mb-2 flex items-center justify-between">
        <div className="font-medium">AI-analys</div>
        {!aiBusy(a) && !open && <button className="text-[13px] font-medium text-ember hover:underline" onClick={() => setOpen(true)}>Analysera…</button>}
      </div>
      <div className="space-y-1">
        {row("Apple Vision", a.vision, visionText, !!status?.ai.vision)}
        {row("Claude", a.claude, AI_LABEL[a.claude] ?? a.claude, !!status?.ai.claude)}
      </div>
      {open && (
        <div className="mt-2 rounded-lg border border-line p-1">
          <AnalyzePanel compact ids={[a.id]} onDone={async (m) => {
            setOpen(false); setMsg(m);
            onChange(await api<Detail>(`assets/${a.id}`));
          }} />
        </div>
      )}
      {msg && <p className="mt-1 text-[12px] text-soft">{msg}</p>}
    </div>
  );
}

// ---------- Metadata ----------
const FIELD_NAME: Record<string, string> = { title: "titel", alt: "alt-text", description: "beskrivning" };

function MetaForm({ d, onChange }: { d: Detail; onChange: (d: Detail) => void }) {
  const { status } = useApp();
  const a = d.asset;
  const [v, setV] = useState(a);
  const [saved, setSaved] = useState<string | null>(null);
  useEffect(() => setV(a), [a]);
  const aiFields = JSON.parse(a.ai_written || "[]") as string[];
  const aiBy = a.ai_text_source === "claude" ? "Claude" : "Apple Intelligence";
  const save = async (k: keyof typeof a, value?: unknown) => {
    const val = value ?? v[k];
    if (val === a[k]) return;
    onChange(await api<Detail>(`assets/${a.id}`, { method: "PATCH", json: { [k]: val } }));
    setSaved(k);
    setTimeout(() => setSaved(null), 1500);
  };
  const approve = async (fields?: string[]) =>
    onChange(await api<Detail>(`assets/${a.id}/ai-text`, { method: "POST", json: { fields } }));
  /** AI-skrivna fält får en tonad bakgrund tills någon godkänner eller ändrar dem. */
  const aiCls = (k: string) => (aiFields.includes(k) ? "!border-ember/40 !bg-ember-soft/40" : "");
  const AiTag = ({ k }: { k: string }) => aiFields.includes(k) ? (
    <span className="ml-1.5 inline-flex items-center gap-1 rounded bg-ember-soft px-1.5 py-px text-[11px] font-medium text-ember">
      <Sparkles size={10} /> AI-utkast
      <button onClick={() => approve([k])} className="ml-0.5 underline-offset-2 hover:underline" title="Godkänn texten som den är">Godkänn</button>
    </span>
  ) : null;
  const input = (k: "title" | "photographer" | "place" | "license_note" | "consent_note", label: string, ph = "") => (
    <div>
      <label className="label">{label} <AiTag k={k} /> {saved === k && <span className="text-ok">✓ sparat</span>}</label>
      <input className={`field ${aiCls(k)}`} value={v[k] ?? ""} placeholder={ph} onChange={(e) => setV({ ...v, [k]: e.target.value })} onBlur={() => save(k)} />
    </div>
  );
  const altLen = v.alt.length;
  return (
    <div className="card space-y-3 p-4">
      <div className="h-section">Information</div>
      {aiFields.length > 0 && (
        <div className="flex items-start gap-2 rounded-lg bg-ember-soft/60 px-3 py-2 text-[13px]">
          <Sparkles size={14} className="mt-0.5 shrink-0 text-ember" />
          <span className="flex-1 text-soft">
            {aiBy} har skrivit {aiFields.map((f) => FIELD_NAME[f]).join(", ")} utifrån analysen. Läs igenom och justera – texten
            räknas som granskad när du ändrar den eller godkänner den.
          </span>
          <button onClick={() => approve()} className="shrink-0 font-medium text-ember hover:underline">Godkänn alla</button>
        </div>
      )}
      {input("title", "Titel")}
      <div>
        <label className="label flex justify-between">
          <span>Alt-text <span className="font-normal text-muted">– läses upp av skärmläsare</span> <AiTag k="alt" /> {saved === "alt" && <span className="text-ok">✓ sparat</span>}</span>
          <span className={altLen > 125 ? "text-warn" : "text-muted"}>{altLen}/125</span>
        </label>
        <textarea className={`field min-h-[64px] ${aiCls("alt")}`} value={v.alt} onChange={(e) => setV({ ...v, alt: e.target.value })} onBlur={() => save("alt")}
          placeholder="Beskriv vad bilden visar, t.ex. ”Rådgivare och kund vid ett bord på kontoret i Sölvesborg”" />
      </div>
      <div>
        <label className="label">Beskrivning <AiTag k="description" /> {saved === "description" && <span className="text-ok">✓ sparat</span>}</label>
        <textarea className={`field min-h-[56px] ${aiCls("description")}`} value={v.description} onChange={(e) => setV({ ...v, description: e.target.value })} onBlur={() => save("description")}
          placeholder="Sammanhang, vilka som syns (med samtycke), när bilden får användas…" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        {input("photographer", "Fotograf")}
        {input("place", "Plats")}
      </div>

      <div className="grid grid-cols-2 gap-3 border-t border-line pt-3">
        <div>
          <label className="label">Licens</label>
          <select className="field" value={v.license} onChange={(e) => save("license", e.target.value)}>
            {Object.entries(status?.licenses ?? {}).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Licensen gäller till</label>
          <input type="date" className="field" value={v.license_expires ?? ""} onChange={(e) => save("license_expires", e.target.value)} />
        </div>
      </div>
      {input("license_note", "Licensvillkor", "T.ex. ”Får användas i bankens egna kanaler, ej tryck”")}

      <div className="border-t border-line pt-3">
        <label className="label flex items-center gap-1.5"><Users size={13} /> Personer i bild (GDPR)</label>
        <select className={`field ${v.consent === "saknas" ? "!border-bad/50 !bg-bad-soft" : ""}`} value={v.consent} onChange={(e) => save("consent", e.target.value)}>
          {Object.entries(status?.consents ?? {}).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        {(a.people_count ?? 0) > 0 && <p className="mt-1 text-[12px] text-soft">AI hittade {a.people_count} ansikte{a.people_count === 1 ? "" : "n"} i bilden.</p>}
      </div>
      {(v.consent === "samtycke" || v.consent === "saknas") && input("consent_note", "Samtycke – vem, när, var det förvaras", "T.ex. ”Anna Svensson, signerat 2026-05-12, diarienr 123”")}
    </div>
  );
}

// ---------- Inbäddning ----------
const MODES = [
  { k: "html", label: "HTML" },
  { k: "component", label: "Webbkomponent" },
  { k: "url", label: "Bildlänk" },
  { k: "meta", label: "Metadata-länk" },
] as const;
const SIZES: Record<string, string> = {
  "Full bredd": "100vw",
  "Innehållsspalt": "(max-width: 800px) 100vw, 800px",
  "Halv bredd": "(max-width: 800px) 100vw, 50vw",
  "Kort / puff": "(max-width: 600px) 100vw, 400px",
};

function EmbedPanel({ d }: { d: Detail }) {
  const a = d.asset;
  const [mode, setMode] = useState<(typeof MODES)[number]["k"]>("html");
  const [ratio, setRatio] = useState("");
  const [sizes, setSizes] = useState("Innehållsspalt");
  const [caption, setCaption] = useState(true);
  const [out, setOut] = useState<{ html: string; component: string; meta: string; url: string } | null>(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    const p = new URLSearchParams({ ratio, sizes: SIZES[sizes]!, caption: String(caption) });
    api<typeof out>(`assets/${a.id}/embed?${p}`).then(setOut);
  }, [a.id, a.updated_at, ratio, sizes, caption]);
  const code = out ? out[mode] : "";
  const copy = async () => { await navigator.clipboard.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 1500); };

  return (
    <div className="card p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="h-section">Bädda in</div>
        <Link href="/badda-in" className="flex items-center gap-1 text-[12px] text-soft hover:text-ink"><Info size={13} /> Så funkar det</Link>
      </div>
      {d.blocked && <p className="mb-3 rounded-lg bg-warn-soft px-3 py-2 text-[13px] text-warn">Koden fungerar först när bilden är publicerad ({d.blocked}).</p>}
      <div className="mb-3 flex flex-wrap gap-1 rounded-lg bg-mist p-1">
        {MODES.map((m) => (
          <button key={m.k} onClick={() => setMode(m.k)} className={`flex-1 rounded-md px-2 py-1 text-[13px] font-medium ${mode === m.k ? "bg-card shadow-sm" : "text-soft"}`}>{m.label}</button>
        ))}
      </div>
      {(mode === "html" || mode === "component" || mode === "url") && (
        <div className="mb-3 grid grid-cols-2 gap-2">
          <div>
            <label className="label">Beskärning</label>
            <select className="field" value={ratio} onChange={(e) => setRatio(e.target.value)}>
              <option value="">Original</option>
              {["16:9", "3:2", "4:3", "1:1", "4:5", "21:9"].map((r) => <option key={r}>{r}</option>)}
            </select>
          </div>
          {mode !== "url" && (
            <div>
              <label className="label">Placering på sidan</label>
              <select className="field" value={sizes} onChange={(e) => setSizes(e.target.value)}>
                {Object.keys(SIZES).map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
          )}
        </div>
      )}
      {mode !== "url" && mode !== "meta" && (
        <label className="mb-3 flex items-center gap-2 text-[13px]">
          <input type="checkbox" checked={caption} onChange={(e) => setCaption(e.target.checked)} className="accent-ember" /> Visa fotografbyline
        </label>
      )}
      <div className="relative">
        <pre className="max-h-56 overflow-auto whitespace-pre-wrap break-all rounded-lg bg-ink p-3 pr-20 font-mono text-[11.5px] leading-relaxed text-stone-200">{code}</pre>
        <button onClick={copy} className="absolute right-2 top-2 flex items-center gap-1 rounded-md bg-white/15 px-2 py-1 text-[12px] font-medium text-white hover:bg-white/25">
          {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? "Kopierad" : "Kopiera"}
        </button>
      </div>
      <p className="mt-2 text-[12px] leading-relaxed text-soft">
        {mode === "html" && "Klistra in i webbplatsens HTML- eller kodblock. Ren HTML med responsiva storlekar (AVIF/WebP/JPG), alt-text och byline – fungerar utan JavaScript och är bäst för SEO och tillgänglighet."}
        {mode === "component" && "Hämtar alt-text, byline och storlekar live från bildbanken vid varje sidvisning – ändrar du texten här slår det igenom direkt på sajten. Kräver att webbplatsen tillåter skript i kodblocket."}
        {mode === "url" && "Direktlänk till bilden. Ändra w= för bredd (320–2560) och filändelsen för format (.webp, .avif, .jpg)."}
        {mode === "meta" && "All metadata som JSON: alt-text, fotograf, licens, taggar, fokuspunkt, srcset och schema.org. Varje bildlänk pekar också hit via HTTP-headern Link: rel=\"describedby\"."}
      </p>
      {mode === "meta" && <a href={d.links.meta} target="_blank" className="mt-2 inline-flex items-center gap-1 text-[13px] font-medium text-ember hover:underline">Öppna JSON <ExternalLink size={12} /></a>}
    </div>
  );
}

// ---------- Användning ----------
function Usage({ d }: { d: Detail }) {
  return (
    <div className="card p-4">
      <div className="h-section mb-2">Används på</div>
      {d.usages.length === 0 ? (
        <p className="text-[13px] text-muted">Inga sidor har laddat bilden ännu. Sidor registreras automatiskt när någon besöker dem.</p>
      ) : (
        <ul className="space-y-1.5 text-[13px]">
          {d.usages.map((u) => (
            <li key={u.page_url} className="flex items-center justify-between gap-3">
              <a href={u.page_url} target="_blank" className="truncate text-ember hover:underline">{u.page_url.replace(/^https?:\/\//, "")}</a>
              <span className="shrink-0 text-muted">{u.hits} visn. · {localTime(u.last_seen, false)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Similar({ d }: { d: Detail }) {
  if (!d.similar.length) return null;
  return (
    <div className="card p-4">
      <div className="h-section mb-2">Liknande bilder – möjliga dubbletter</div>
      <div className="grid grid-cols-4 gap-2">
        {d.similar.map((s) => (
          <Link key={s.id} href={`/bild/${s.id}`} title={`${s.title} (avstånd ${s.distance})`}>
            <img src={img(s, 320, "1:1")} className="aspect-square w-full rounded-lg object-cover" style={{ background: s.color }} />
          </Link>
        ))}
      </div>
    </div>
  );
}

// ---------- Åtgärder ----------
function Actions({ d, onChange }: { d: Detail; onChange: (d: Detail) => void }) {
  const a = d.asset;
  const [, navigate] = useLocation();
  const { bump } = useApp();
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);
  const [exporting, setExporting] = useState<"social" | "pptx" | null>(null);
  const replace = async (file?: File) => {
    if (!file) return;
    setBusy("replace");
    const fd = new FormData(); fd.append("file", file);
    try { onChange(await api<Detail>(`assets/${a.id}/replace`, { method: "POST", body: fd })); bump(); }
    finally { setBusy(""); }
  };
  return (
    <div className="card p-4" data-replace-drop
      onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); replace(e.dataTransfer.files[0]); }}>
      <div className="h-section mb-2">Åtgärder</div>
      <div className="flex flex-wrap gap-2">
        <button className="btn" onClick={() => fileRef.current?.click()} disabled={!!busy}>
          {busy === "replace" ? <Loader2 size={14} className="animate-spin" /> : <Replace size={14} />} Ersätt fil
        </button>
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => replace(e.target.files?.[0])} />
        <a className="btn" href={d.links.original}><Download size={14} /> Original</a>
        <button className="btn" onClick={() => setExporting("social")}><Share2 size={14} /> Sociala medier</button>
        <button className="btn" onClick={() => setExporting("pptx")}><Presentation size={14} /> PowerPoint</button>
        <button className="btn" onClick={async () => onChange(await api<Detail>(`assets/${a.id}/reanalyze`, { method: "POST" }))}>
          <RefreshCw size={14} /> Läs om filinfo
        </button>
        {a.status !== "arkiverad"
          ? <button className="btn" onClick={async () => onChange(await api<Detail>(`assets/${a.id}`, { method: "PATCH", json: { status: "arkiverad" } }))}>Arkivera</button>
          : <button className="btn" onClick={async () => onChange(await api<Detail>(`assets/${a.id}`, { method: "PATCH", json: { status: "utkast" } }))}>Återställ</button>}
        <button className="btn !text-bad" onClick={async () => {
          if (!confirm("Ta bort bilden permanent?")) return;
          try { await api(`assets/${a.id}`, { method: "DELETE" }); bump(); navigate("/"); }
          catch (e) { setErr(e instanceof Error ? e.message : "Kunde inte ta bort"); }
        }}><Trash2 size={14} /> Ta bort</button>
      </div>
      <p className="mt-2 text-[12px] text-soft">”Ersätt fil” behåller alla länkar – sidor där bilden är inbäddad visar den nya bilden utan att någon behöver ändra koden. Du kan också släppa en fil här.</p>
      {err && <p className="mt-2 text-[13px] text-bad">{err}</p>}
      {exporting && <ExportDialog items={[a]} initial={exporting} onClose={() => setExporting(null)} />}
    </div>
  );
}

function History({ d }: { d: Detail }) {
  return (
    <div className="px-1">
      <div className="h-section mb-2">Historik</div>
      <ul className="space-y-1 text-[12.5px] text-soft">
        {d.events.map((e) => (
          <li key={e.id} className="flex gap-3"><span className="w-28 shrink-0 text-muted">{localTime(e.at)}</span><span>{e.detail}</span></li>
        ))}
      </ul>
    </div>
  );
}
